import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Zap, Shield, Crown, ArrowRight, Lock } from 'lucide-react';

export default function PricingPage({ user, onNavigate, onSelectPlan, onRequireLogin, hasActiveAnalysis }) {

  const handleChoosePlan = (planType) => {
    if (!user) {
      onRequireLogin();
      return;
    }
    onNavigate('/checkout', { plan: planType });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Investment in Your Next Job Offer</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
            Transparent Plans for Every Job Search
          </h1>
          <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
            Land more interviews with fact-locked ATS resume optimization. Choose a single application boost or unlock 20 monthly tailored resumes.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {/* Plan 1: Pay Per Resume */}
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
            className="rounded-3xl bg-slate-900/70 border border-slate-800 p-6 sm:p-8 flex flex-col justify-between relative shadow-xl backdrop-blur-sm"
          >
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-indigo-400" />
                    Pay As You Go
                  </span>
                  <h3 className="text-2xl font-bold text-white mt-1">Pay Per Resume</h3>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-800 text-indigo-300 border border-indigo-500/30">
                  Save 67%
                </span>
              </div>

              <p className="text-xs sm:text-sm text-slate-400">
                Ideal for candidates applying to a specific dream role who need one perfectly tailored ATS resume.
              </p>

              <div className="pt-2 border-t border-slate-800/80">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl sm:text-5xl font-black text-white">₹49</span>
                  <span className="text-sm text-slate-500 line-through">₹149</span>
                  <span className="text-xs text-slate-400 font-medium">/ resume</span>
                </div>
                <p className="text-[11px] text-emerald-400 font-semibold mt-1">One-time payment • No auto-renewal</p>
              </div>

              <div className="space-y-3 pt-2">
                <p className="text-xs font-bold text-slate-300 uppercase tracking-wider">What's Included:</p>
                <ul className="space-y-2.5 text-xs sm:text-sm text-slate-300">
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>1 Tailored Resume PDF</b> customized for your specific Job Description</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>Fact-Locked Precision</b> (Zero fabrication of dates, companies, or titles)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>Single-Page Dynamic Auto-Fit</b> with zero blank page overflows</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>Executive, Standard, and Modern PDF templates</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>ATS Keyword Gap & Alignment score breakdown</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="pt-8">
              <button
                onClick={() => handleChoosePlan('single_49')}
                className="w-full py-3.5 px-4 rounded-xl font-bold text-sm bg-slate-800 hover:bg-slate-700 text-white transition flex items-center justify-center gap-2 border border-slate-700 hover:border-indigo-500/50 cursor-pointer shadow-md"
              >
                <span>Choose Pay Per Resume (₹49)</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Plan 2: Premium Monthly (Featured) */}
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
            className="rounded-3xl bg-gradient-to-b from-indigo-950/40 via-slate-900/90 to-slate-900 border-2 border-indigo-500 p-6 sm:p-8 flex flex-col justify-between relative shadow-2xl shadow-indigo-950/50 backdrop-blur-sm"
          >
            {/* Popular Badge */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[11px] font-black uppercase tracking-wider shadow-lg flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5 fill-current" />
              <span>Most Popular • 65% OFF</span>
            </div>

            <div className="space-y-6 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-1.5">
                    <Crown className="w-4 h-4 text-amber-400 fill-amber-400" />
                    Job Seeker Package
                  </span>
                  <h3 className="text-2xl font-bold text-white mt-1">Premium Monthly</h3>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-600/30 text-indigo-300 border border-indigo-500/50">
                  ₹39.95 / resume
                </span>
              </div>

              <p className="text-xs sm:text-sm text-slate-300">
                Designed for active job seekers applying across multiple companies, roles, and hiring platforms.
              </p>

              <div className="pt-2 border-t border-indigo-500/20">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl sm:text-5xl font-black text-white">₹799</span>
                  <span className="text-sm text-slate-500 line-through">₹2,299</span>
                  <span className="text-xs text-indigo-300 font-medium">/ month</span>
                </div>
                <p className="text-[11px] text-emerald-400 font-semibold mt-1">Includes 20 Resumes • Valid for 30 Days</p>
              </div>

              <div className="space-y-3 pt-2">
                <p className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Everything in Single, Plus:</p>
                <ul className="space-y-2.5 text-xs sm:text-sm text-slate-200">
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>20 Tailored Resumes</b> (Save ₹180 vs pay-per-resume)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>All 3 Templates Unlocked</b> with instant re-generation & style picker</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>Unlimited ATS Diagnostic Scans</b> & Keyword Alignment Reports</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>Cloud Backup in "My Resumes"</b> for instant re-download anytime</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span><b>Priority Render Queue</b> (Sub-5 second generation)</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="pt-8">
              <button
                onClick={() => handleChoosePlan('premium_799')}
                className="w-full py-3.5 px-4 rounded-xl font-bold text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 cursor-pointer"
              >
                <span>Activate Premium Monthly (₹799)</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Security & Guarantee Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto pt-4 text-center">
          <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 flex items-center gap-3 justify-center">
            <Shield className="w-5 h-5 text-indigo-400 shrink-0" />
            <span className="text-xs text-slate-300 font-medium">256-Bit SSL Encrypted & Verified</span>
          </div>
          <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 flex items-center gap-3 justify-center">
            <Lock className="w-5 h-5 text-emerald-400 shrink-0" />
            <span className="text-xs text-slate-300 font-medium">Fact-Locked Anti-Hallucination Policy</span>
          </div>
          <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 flex items-center gap-3 justify-center">
            <Zap className="w-5 h-5 text-amber-400 shrink-0" />
            <span className="text-xs text-slate-300 font-medium">Instant Activation via Razorpay UPI/Cards</span>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto pt-8 space-y-6">
          <h2 className="text-xl sm:text-2xl font-bold text-center text-white">Frequently Asked Questions</h2>
          <div className="space-y-3">
            <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800 space-y-1">
              <h4 className="text-sm font-semibold text-slate-200">How does the 20-resume quota work?</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                When you purchase the ₹799 Premium Monthly plan, 20 resume optimizations are added directly to your account. You can tailor your resume for 20 different job postings over 30 days.
              </p>
            </div>
            <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800 space-y-1">
              <h4 className="text-sm font-semibold text-slate-200">What is the Fact-Lock guarantee?</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Unlike generic ChatGPT rewriters, CVify preserves your factual career truth: your real companies, dates, degrees, and phone number are locked and never hallucinated.
              </p>
            </div>
            <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800 space-y-1">
              <h4 className="text-sm font-semibold text-slate-200">What payment methods are supported?</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                We accept all Indian UPI apps (Google Pay, PhonePe, Paytm, CRED), Credit/Debit Cards (Visa, MasterCard, RuPay), and NetBanking via Razorpay.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
